import type { SxProps, Theme } from '@mui/material';
import { availabilityTileDotStyles } from '../../components/dashboard/AvailabilityPanel/availabilityPanelStyles';
import { defaultContainerStyling, listPageTableWrapperStyles, secondaryContainerStyling } from '../../styles/sxStyling';
import { appZIndex } from '../../styles/zIndex';

export const ROW_HEIGHT = 24;
export const borderStyle = '1px solid var(--border-color)';

export function daysGridTemplate(dayCount: number) { return { gridTemplateColumns: `repeat(${dayCount}, 1fr)` }; }

// Calendar header
export const headerSx: SxProps = {
  position: 'sticky',
  top: 0,
  zIndex: appZIndex.raised,
  textAlign: 'center',
  borderBottom: '2px solid var(--border-color)',
  bgcolor: 'background.paper',
  '.MuiTypography-root': {
    fontWeight: 'bold',
  },
};

export const employeeHeaderSx: SxProps = {
  ...headerSx,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: ROW_HEIGHT * 2,
  borderRight: borderStyle,
};

export const eventHeaderSx: SxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderBottom: borderStyle,
};

export const dateCellSx: SxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRight: borderStyle,
};

export const datesHeaderContainerSx: SxProps = {
  ...headerSx,
  display: 'flex',
  flexDirection: 'column',
  zIndex: appZIndex.prioritySticky,
  p: 0,
};

export const datesHeaderGridSx: SxProps = {
  display: 'grid',
  height: ROW_HEIGHT,
};

// Calendar layout
export const calendarWrapperSx: SxProps = {
  ...defaultContainerStyling,
  ...listPageTableWrapperStyles,
  overflow: 'hidden',
  border: borderStyle,
  bgcolor: 'background.paper',
};

export const calendarScrollableContentSx: SxProps = {
  overflowY: 'auto',
};

export const calendarToolbarSx: SxProps = {
  display: 'flex',
  alignItems: 'center',
  px: 1,
  py: 0.6,
  borderBottom: borderStyle,
  bgcolor: 'var(--table-header-bg)',
};

export const toolbarDateRangeSx: SxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  gap: 1,
};

export const loadingContainerSx: SxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 500,
};

export const scheduleRowEmployeeContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  pl: 1,
  fontSize: '0.8rem',
  borderTop: borderStyle,
  borderRight: borderStyle,
};

export const scheduleRowEmployeeHeaderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  pt: 1,
};

export const scheduleRowAvatarSx: SxProps<Theme> = {
  width: 20,
  height: 20,
};

export const scheduleRowExpandContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  mt: 'auto',
};

export const scheduleRowExpandButtonSx: SxProps<Theme> = {
  width: 'fit-content',
  p: 0.5,
  '& svg': {
    fontSize: '1rem',
  },
};

export const scheduleGridContainerSx: SxProps<Theme> = {
  position: 'relative',
  display: 'grid',
  gridAutoRows: 'minmax(58px, auto)',
  borderTop: borderStyle,
};

export const scheduleDayBackgroundSx: SxProps = {
  position: 'relative',

};

export const scheduleEventTileContainerSx: SxProps = {
  minWidth: 0, // Required because this is a grid item containing potentially long content.
  zIndex: appZIndex.raised,
  m: 0.75,
  mt: 2.75,
};

// Availability and conflicts
export const scheduleAvailabilityDotSx: SxProps<Theme> = {
  ...availabilityTileDotStyles,
  position: 'absolute',
  top: 10,
  left: '50%',
  zIndex: appZIndex.base,
  transform: 'translateX(-50%)',
};

export const scheduleAvailabilityDotCenteredSx: SxProps<Theme> = {
  ...scheduleAvailabilityDotSx,
  top: '50%',
  transform: 'translate(-50%, -50%)',
};

export const scheduleConflictIconSx: SxProps<Theme> = {
  position: 'absolute',
  top: 4,
  left: '50%',
  zIndex: appZIndex.base,
  fontSize: 16,
  transform: 'translateX(-50%)',
};

// Schedule row tile
interface ScheduleRowTileIndicatorOptions {
  color: string;
  stripeColor: string;
  striped?: boolean;
}

export const scheduleRowTileContainerSx = {
  ...secondaryContainerStyling,
  display: 'flex',
  height: '100%',
  maxHeight: '5.4rem',
  overflow: 'hidden',
  border: '1px solid var(--border-light)',
  backgroundColor: 'background.paper',
  fontSize: '0.875rem',
};

export const scheduleRowTileIndicatorSx = ({ color, stripeColor, striped = false }: ScheduleRowTileIndicatorOptions): SxProps<Theme> => ({
  width: 5,
  flexShrink: 0,
  alignSelf: 'stretch',
  backgroundColor: color,
  backgroundImage: striped
    ? `repeating-linear-gradient(
        135deg,
        ${color} 0,
        ${color} 3px,
        ${stripeColor} 3px,
        ${stripeColor} 5px
      )`
    : 'none',
});

export const scheduleRowTileContentSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: 1,
  minWidth: 0, // Required so this flex item can shrink beside the status indicator.
  overflow: 'hidden',
  gap: 0.5,
  px: 1.5,
};

export const scheduleRowTileHeaderSx = {
  display: 'flex',
};

export const scheduleRowTileHeaderLabelSx = {
  display: 'block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textDecoration: 'underline',
  fontSize: '1em',
  fontWeight: 500,
};

export const scheduleRowTileTextContainerSx = {
  overflow: 'hidden',
  color: 'var(--text-secondary)',
};

export const scheduleRowTileSecondaryTextSx = {
  display: 'flex',
  alignItems: 'center',
  maxHeight: '1.4em',
};

export const scheduleRowTileSecondaryLabelSx = {
  minWidth: 0, // Required for ellipsis inside the icon/label flex row.
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '0.85em',
  color: 'text.secondary',
};

export const scheduleRowTileSecondaryIconSx = {
  flexShrink: 0,
  fontSize: '1.1em',
  mr: 0.5,
};
